import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

/**
 * AddFriendModal - FaceTime-style code entry for adding a friend
 *
 * Allows users to enter a 4-digit FRIEND CODE to send a friend request.
 * This is different from JoinFriendModal which handles SESSION codes.
 *
 * Flow:
 * 1. User enters 4-digit friend code
 * 2. System looks up code (rate limited: 5 attempts/hour)
 * 3. Shows found user with confirmation
 * 4. User confirms to send friend request
 *
 * Design: Mirrors JoinFriendModal exactly per UX principles:
 * - One Primary Action Per Screen
 * - FaceTime-style simplicity
 * - Dark theme, iOS 18 aesthetic
 *
 * FERPA/COPPA: No public search, code-based only, requires acceptance.
 */
const AddFriendModal = ({ isOpen, onClose, onFriendAdded }) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [isLooking, setIsLooking] = useState(false);
  const [error, setError] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [foundUser, setFoundUser] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode(['', '', '', '']);
      setError(null);
      setFoundUser(null);
      setSuccess(false);
      setAttemptsRemaining(null);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-lookup when code is complete
  useEffect(() => {
    const fullCode = code.join('');
    if (fullCode.length === 4 && code.every(d => d !== '') && !foundUser && !isLooking) {
      lookupCode(fullCode);
    }
  }, [code, foundUser, isLooking]);

  const handleDigitChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError(null);

    // Auto-advance to next input
    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 4).split('');
        const newCode = ['', '', '', ''];
        digits.forEach((d, i) => newCode[i] = d);
        setCode(newCode);
        if (digits.length === 4) {
          inputRefs[3].current?.focus();
        } else if (digits.length > 0) {
          inputRefs[digits.length]?.current?.focus();
        }
      });
    }
  };

  const lookupCode = async (fullCode) => {
    setIsLooking(true);
    setError(null);

    try {
      const response = await axios.post('/api/friends/lookup-code', {
        code: fullCode
      });

      if (response.data.success && response.data.found) {
        setFoundUser(response.data.user);
        setAttemptsRemaining(response.data.attempts_remaining);
      } else {
        setError(response.data.error || 'Code not found');
        setAttemptsRemaining(response.data.attempts_remaining);
        resetCode();
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
        setAttemptsRemaining(0);
      } else if (err.response?.status === 404) {
        setError('Code not found');
        setAttemptsRemaining(err.response.data?.attempts_remaining);
        resetCode();
      } else {
        setError(err.response?.data?.error || 'Connection error');
        resetCode();
      }
    } finally {
      setIsLooking(false);
    }
  };

  const resetCode = () => {
    setCode(['', '', '', '']);
    setTimeout(() => inputRefs[0].current?.focus(), 100);
  };

  const sendFriendRequest = async () => {
    if (!foundUser) return;

    setIsSending(true);
    setError(null);

    try {
      const response = await axios.post('/api/friends/request', {
        friend_user_id: foundUser.user_id
      });

      if (response.data.success) {
        setSuccess(true);
        // Notify parent
        if (onFriendAdded) {
          onFriendAdded(foundUser);
        }
        // Close after brief delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to send request');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send request');
    } finally {
      setIsSending(false);
    }
  };

  const goBack = () => {
    setFoundUser(null);
    resetCode();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#1c1c1e',
            borderRadius: '24px',
            padding: '32px',
            width: '100%',
            maxWidth: '340px',
            textAlign: 'center'
          }}
        >
          {/* Success State */}
          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                ✓
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 8px 0'
              }}>
                Request Sent!
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px',
                margin: 0
              }}>
                {foundUser?.display_name} will see your request
              </p>
            </motion.div>
          ) : foundUser ? (
            /* Confirmation State - User found, confirm to send request */
            <>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: 600,
                margin: '0 auto 16px'
              }}>
                {foundUser.avatar_initial}
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: 600,
                margin: '0 0 8px 0'
              }}>
                {foundUser.display_name}
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px',
                margin: '0 0 24px 0'
              }}>
                Send a friend request?
              </p>

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    color: '#ff453a',
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}
                >
                  {error}
                </motion.div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={goBack}
                  disabled={isSending}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={sendFriendRequest}
                  disabled={isSending}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #297A92, #42909E)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: isSending ? 'default' : 'pointer',
                    opacity: isSending ? 0.7 : 1
                  }}
                >
                  {isSending ? 'Sending...' : 'Add Friend'}
                </motion.button>
              </div>
            </>
          ) : (
            /* Code Entry State */
            <>
              {/* Header */}
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '22px',
                fontWeight: 600,
                margin: '0 0 8px 0'
              }}>
                Add Friend
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '15px',
                margin: '0 0 28px 0'
              }}>
                Enter your friend's 4-digit code
              </p>

              {/* Code Input - 4 separate boxes */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isLooking || attemptsRemaining === 0}
                    style={{
                      width: '56px',
                      height: '64px',
                      fontSize: '28px',
                      fontWeight: 700,
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: error ? '2px solid #ff453a' : '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      outline: 'none',
                      caretColor: '#297A92',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#297A92';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = error ? '#ff453a' : 'rgba(255, 255, 255, 0.2)';
                    }}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    color: '#ff453a',
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}
                >
                  {error}
                </motion.div>
              )}

              {/* Rate limit warning */}
              {attemptsRemaining !== null && attemptsRemaining <= 2 && attemptsRemaining > 0 && (
                <div style={{
                  color: '#ff9f0a',
                  fontSize: '12px',
                  marginBottom: '12px'
                }}>
                  {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining
                </div>
              )}

              {/* Loading State */}
              {isLooking && (
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '15px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    style={{ width: '16px', height: '16px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                  </motion.div>
                  Looking up code...
                </div>
              )}

              {/* Cancel Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                Cancel
              </motion.button>

              {/* Help text */}
              <p style={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '12px',
                marginTop: '20px'
              }}>
                Ask your friend to share their code from the Friends menu
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddFriendModal;
